import { CommonModule } from '@angular/common';
import { Component, EnvironmentInjector, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { ConfirmModalComponent } from '../../../Design/confirm-modal.component';
import { ModalService } from '../../../services/design/modal.service';
import { SnackbarService } from '../../../services/design/snackbar.service';
import {
  DeliveryAreaPoint,
  Store,
  StoreService,
} from '../../../services/stores/store.service';

@Component({
  selector: 'app-store-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './store-list.component.html',
  styleUrls: ['./store-list.component.scss'],
})
export class StoreListComponent implements OnInit {
  readonly #storeService = inject(StoreService);
  readonly #router = inject(Router);
  readonly #modalService = inject(ModalService);
  readonly #snackbar = inject(SnackbarService);
  private envInjector = inject(EnvironmentInjector);

  public searchTerm = '';
  public status: 'active' | 'inactive' | 'all' = 'active';
  public page = 1;
  public pageSize = 10;
  public totalPages = 1;
  public totalCount = 0;
  public loading = false;
  public downloadingStoreId: number | null = null;

  sortBy: keyof Store = 'name';
  order: 'ASC' | 'DESC' = 'ASC';

  public stores: Store[] = [];
  public closedStoreIds = new Set<number>();

  ngOnInit(): void {
    this.loadStores();
  }

  get activeCount(): number {
    return this.stores.filter((s) => s.is_active).length;
  }

  toggleSort() {
    this.order = this.order === 'ASC' ? 'DESC' : 'ASC';
    this.loadStores();
  }

  onSearchChange() {
    this.page = 1;
    this.loadStores();
  }

  changePage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.page = page;
    this.loadStores();
  }
  navigate(route: string) {
    console.log('Navigating to', route);
    this.#router.navigate([route]);
  }

  loadStores() {
    this.loading = true;
    this.#storeService
      .getAllStores(
        this.page,
        this.pageSize,
        this.searchTerm,
        this.sortBy,
        this.order,
        this.status
      )
      .subscribe({
        next: (res) => {
          this.stores = res.data;
          this.totalCount = res.count;
          this.totalPages = Math.ceil(this.totalCount / this.pageSize);
          this.loading = false;
          void this.loadClosedStoreIds();
        },
        error: (err) => {
          console.error('Failed to load stores', err);
          this.#snackbar.error('Failed to load stores');
          this.loading = false;
        },
      });
  }

  isClosedNow(store: Store): boolean {
    return this.closedStoreIds.has(Number(store.id));
  }

  private async loadClosedStoreIds(): Promise<void> {
    try {
      const ids = await this.#storeService.currentlyClosedStoreIds();
      this.closedStoreIds = new Set(ids.filter((id) => Number.isFinite(id)));
    } catch {
      this.closedStoreIds = new Set();
    }
  }

  editStore(id: number) {
    this.#router.navigate(['stores/add', id]);
  }

  async downloadDeliveryMap(store: Store): Promise<void> {
    const latitude = Number(store.latitude);
    const longitude = Number(store.longitude);
    const radiusKm = Number(store.store_radius);
    const deliveryArea = this.getDeliveryArea(store);

    if (
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude) ||
      Math.abs(latitude) > 90 ||
      Math.abs(longitude) > 180 ||
      (latitude === 0 && longitude === 0)
    ) {
      this.#snackbar.warning('Add valid store coordinates before downloading');
      return;
    }

    if (
      deliveryArea.length < 3 &&
      (!Number.isFinite(radiusKm) || radiusKm <= 0)
    ) {
      this.#snackbar.warning('Draw a delivery area before downloading');
      return;
    }

    if (!environment.googleMapsApiKey) {
      this.#snackbar.error('Google Maps API key is not configured');
      return;
    }

    const mapUrl = this.buildStaticMapUrl(
      latitude,
      longitude,
      deliveryArea,
      radiusKm,
      environment.googleMapsApiKey
    );
    const filename = `${this.toFilename(store.name)}-delivery-area.png`;

    this.downloadingStoreId = store.id;
    try {
      const response = await fetch(mapUrl);
      if (!response.ok) {
        throw new Error(`Maps Static API returned ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) {
        throw new Error('Maps Static API did not return an image');
      }

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
      this.#snackbar.success('Delivery area map downloaded');
    } catch (error) {
      console.error('Failed to download delivery area map', error);
      this.#snackbar.error(
        'Could not download the map. Ensure Maps Static API is enabled.'
      );
    } finally {
      this.downloadingStoreId = null;
    }
  }

  private buildStaticMapUrl(
    latitude: number,
    longitude: number,
    deliveryArea: DeliveryAreaPoint[],
    radiusKm: number,
    apiKey: string
  ): string {
    const coveragePath =
      deliveryArea.length >= 3
        ? this.buildDeliveryAreaPath(deliveryArea)
        : this.buildRadiusPath(latitude, longitude, radiusKm);
    const params = new URLSearchParams({
      size: '640x640',
      scale: '2',
      format: 'png',
      maptype: 'roadmap',
      markers: `size:mid|color:0x248842|label:S|${latitude.toFixed(
        6
      )},${longitude.toFixed(6)}`,
      path: `fillcolor:0x24884233|color:0x248842ff|weight:4|${coveragePath}`,
      key: apiKey,
    });

    return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
  }

  getDeliveryArea(store: Store): DeliveryAreaPoint[] {
    if (!Array.isArray(store.delivery_area)) return [];

    return store.delivery_area
      .map((point) => ({
        lat: Number(point?.lat),
        lng: Number(point?.lng),
      }))
      .filter(
        (point) =>
          Number.isFinite(point.lat) &&
          Number.isFinite(point.lng) &&
          Math.abs(point.lat) <= 90 &&
          Math.abs(point.lng) <= 180
      );
  }

  private buildDeliveryAreaPath(deliveryArea: DeliveryAreaPoint[]): string {
    const closedArea = [...deliveryArea, deliveryArea[0]];
    return closedArea
      .map((point) => `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`)
      .join('|');
  }

  private buildRadiusPath(
    latitude: number,
    longitude: number,
    radiusKm: number
  ): string {
    const earthRadiusKm = 6371;
    const angularDistance = radiusKm / earthRadiusKm;
    const latitudeRadians = (latitude * Math.PI) / 180;
    const longitudeRadians = (longitude * Math.PI) / 180;
    const points: string[] = [];

    for (let index = 0; index <= 72; index += 1) {
      const bearing = ((index * 5) * Math.PI) / 180;
      const pointLatitude = Math.asin(
        Math.sin(latitudeRadians) * Math.cos(angularDistance) +
          Math.cos(latitudeRadians) *
            Math.sin(angularDistance) *
            Math.cos(bearing)
      );
      const pointLongitude =
        longitudeRadians +
        Math.atan2(
          Math.sin(bearing) *
            Math.sin(angularDistance) *
            Math.cos(latitudeRadians),
          Math.cos(angularDistance) -
            Math.sin(latitudeRadians) * Math.sin(pointLatitude)
        );

      points.push(
        `${((pointLatitude * 180) / Math.PI).toFixed(6)},${(
          (pointLongitude * 180) /
          Math.PI
        ).toFixed(6)}`
      );
    }

    return points.join('|');
  }

  private toFilename(value: string): string {
    return (
      value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'store'
    );
  }

  deleteStore(store: Store) {
    this.#modalService
      .open(
        ConfirmModalComponent,
        {
          modalData: {
            heading: 'Delete Store',
            itemName: store.name,
            btnText: 'Delete',
            cancelText: 'Cancel',
            btnType: 'delete',
            returnData: { storeId: store.id },
          },
        },
        this.envInjector
      )
      .subscribe((result) => {
        if (!result) return;

        this.#storeService.deleteStore(result.storeId);
      });
  }

  async toggleStoreStatus(store: Store) {
    const updatedStatus = !store.is_active;

    try {
      await this.#storeService.updateStore(store.id, {
        is_active: updatedStatus,
      });

      store.is_active = updatedStatus;
      this.#snackbar.success(
        store.is_active ? 'Store activated' : 'Store deactivated'
      );
    } catch (err) {
      console.error('Failed to update store status', err);
      this.#snackbar.error('Failed to update store status');
    }
  }
}
